import { createGlobalStyle, DefaultTheme } from "styled-components";

// Converts a hex color ("#ea8522") to an "r, g, b" triplet ("234, 133, 34"),
// for tokens that need to be used inside rgba(var(--token), alpha).
// Falls back to the input unchanged if it isn't a recognizable hex string
// (e.g. it's already "transparent" or an rgb()/rgba() value).
const hexToRgbTriplet = (hex: string): string => {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return hex;
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `${r}, ${g}, ${b}`;
};

const GlobalStyle = createGlobalStyle<{ theme: DefaultTheme }>`
@import 'datatables.net-bs5';
@import 'datatables.net-responsive-bs5';
@import 'datatables.net-select-bs5';

::placeholder {
    color: #999;
}

:root {
    /* Spacing */
    --space-01: ${(props) => props.theme.spacePx.sp01};
    --space-02: ${(props) => props.theme.spacePx.sp02};
    --space-03: ${(props) => props.theme.spacePx.sp03};
    --space-04: ${(props) => props.theme.spacePx.sp04};
    --space-05: ${(props) => props.theme.spacePx.sp05};
    --space-06: ${(props) => props.theme.spacePx.sp06};
    --space-07: ${(props) => props.theme.spacePx.sp07};
    --space-08: ${(props) => props.theme.spacePx.sp08};
    --space-09: ${(props) => props.theme.spacePx.sp09};
    --space-icon-gap: ${(props) => props.theme.spacingScale.iconGap};
    --space-tight-padding: ${(props) => props.theme.spacingScale.tightPadding};
    --space-form-field-gap: ${(props) => props.theme.spacingScale.formFieldGap};
    --space-card-padding: ${(props) => props.theme.spacingScale.cardPadding};
    --space-section-gap: ${(props) => props.theme.spacingScale.sectionGap};
    --space-between-cards: ${(props) => props.theme.spacingScale.betweenCards};
    --space-major-section-break: ${(props) => props.theme.spacingScale.majorSectionBreak};
    --space-page-margin: ${(props) => props.theme.spacingScale.pageMargin};
    --space-hero-spacing: ${(props) => props.theme.spacingScale.heroSpacing};

    /* Border radius */
    --radius-sm: ${(props) => props.theme.shape.borderRadiusSm}px;
    --radius-md: ${(props) => props.theme.shape.borderRadiusMd}px;
    --radius-lg: ${(props) => props.theme.shape.borderRadiusLg}px;

    /* Font */
    --font-family: ${(props) => props.theme.font.family};
    --font-size-xs: ${(props) => props.theme.font.sizes.xs};
    --font-size-sm: ${(props) => props.theme.font.sizes.sm};
    --font-size-md: ${(props) => props.theme.font.sizes.md};
    --font-size-lg: ${(props) => props.theme.font.sizes.lg};
    --font-size-xl: ${(props) => props.theme.font.sizes.xl};
    --font-size-xxl: ${(props) => props.theme.font.sizes.xxl};
    --font-weight-light: ${(props) => props.theme.font.weights.light};
    --font-weight-regular: ${(props) => props.theme.font.weights.regular};
    --font-weight-semibold: ${(props) => props.theme.font.weights.semiBold};
    --font-weight-bold: ${(props) => props.theme.font.weights.bold};

    /* Core colors */
    --color-white: ${(props) => hexToRgbTriplet(props.theme.palette.white)};
    --color-white-solid: ${(props) => props.theme.palette.white};
    --color-black: ${(props) => hexToRgbTriplet(props.theme.palette.black)};
    --color-grey: ${(props) => props.theme.palette.grey};
    --color-grey-rgb: ${(props) => hexToRgbTriplet(props.theme.palette.grey)};
    --color-grey-light: #e1e9ef;
    --color-transparent: ${(props) => props.theme.palette.background.transparent};

    /* Brand accent */
    --color-accent-dark: ${(props) => props.theme.palette.accent.dark};
    --color-accent-dark-rgb: ${(props) => hexToRgbTriplet(props.theme.palette.accent.dark)};
    --color-accent-light: ${(props) => props.theme.palette.accent.light};

    /* Background */
    --color-background: ${(props) => props.theme.palette.background.main};
    --color-background-light: ${(props) => props.theme.palette.background.light};
    --color-background-dark: ${(props) => props.theme.palette.background.dark};

    /* Text */
    --color-text: ${(props) => props.theme.palette.text.main};
    --color-text-light: ${(props) => props.theme.palette.text.light};
    --color-text-dark: ${(props) => props.theme.palette.text.dark};
    --color-text-disabled: ${(props) => props.theme.palette.text.disabled};
    --color-text-hint: ${(props) => props.theme.palette.text.hint};
    --color-text-secondary: ${(props) => props.theme.palette.text.secondary};
    --color-text-strong: ${(props) => props.theme.palette.text.strong};

    /* Disabled */
    --color-disabled-light: ${(props) => props.theme.palette.disabled.light};
    --color-disabled-dark: ${(props) => props.theme.palette.disabled.dark};

    /* Link */
    --color-link: ${(props) => props.theme.palette.link.main};
    --color-link-light: ${(props) => props.theme.palette.link.light};
    --color-link-dark: ${(props) => props.theme.palette.link.dark};

    /* Information */
    --color-information: ${(props) => props.theme.palette.information.main};
    --color-information-light: ${(props) => props.theme.palette.information.light};
    --color-information-dark: ${(props) => props.theme.palette.information.dark};

    /* Success */
    --color-success: ${(props) => props.theme.palette.success.main};
    --color-success-light: ${(props) => props.theme.palette.success.light};
    --color-success-dark: ${(props) => props.theme.palette.success.dark};

    /* Danger */
    --color-danger: ${(props) => props.theme.palette.danger.main};
    --color-danger-light: ${(props) => props.theme.palette.danger.light};
    --color-danger-dark: ${(props) => props.theme.palette.danger.dark};

    /* Warning */
    --color-warning: ${(props) => props.theme.palette.warning.main};
    --color-warning-light: ${(props) => props.theme.palette.warning.light};
    --color-warning-dark: ${(props) => props.theme.palette.warning.dark};

    /* Divider */
    --color-divider: ${(props) => props.theme.palette.divider.main};
    --color-divider-light: ${(props) => props.theme.palette.divider.light};
    --color-divider-dark: ${(props) => props.theme.palette.divider.dark};
    --color-divider-subtle: ${(props) => props.theme.palette.divider.subtle};

    /* Tab */
    --color-tab: ${(props) => props.theme.palette.tab.main};
    --color-tab-light: ${(props) => props.theme.palette.tab.light};
    --color-tab-dark: ${(props) => props.theme.palette.tab.dark};

    /* Icon */
    --color-icon-muted: ${(props) => props.theme.palette.icon.muted};
    --color-icon-parent-accent: ${(props) => props.theme.palette.icon.parentAccent};

    /* Shadows */
    --shadow-button-hover: ${(props) => props.theme.shadowTypes.buttonHover};
    --shadow-button-focus: ${(props) => props.theme.shadowTypes.buttonFocus};
    --shadow-popover: ${(props) => props.theme.shadowTypes.popover};
    --shadow-image-hover: ${(props) => props.theme.shadowTypes.imageHover};

    /* Date picker (derived) */
    --date-picker-background: ${(props) => props.theme.palette.background.main};
    --date-picker-border: ${(props) => props.theme.palette.divider.subtle};
    --date-picker-border-focus: ${(props) => props.theme.palette.accent.dark};
    --date-picker-icon: ${(props) => props.theme.palette.accent.dark};
    --date-picker-text: ${(props) => props.theme.palette.text.main};
    --date-picker-focus-shadow: ${(props) => props.theme.shadowTypes.buttonFocus};
}

body{
    background-color:${(props) => props.theme.palette.background.main};
    color:${(props) => props.theme.palette.text.main};
    font-family:${(props) => props.theme.font.family};
}

.themed-date-input {
    accent-color: var(--date-picker-border-focus);
    background-color: var(--date-picker-background);
    border-color: var(--date-picker-border);
    color: var(--date-picker-text);
    color-scheme: light;
}

.themed-date-input:focus {
    border-color: var(--date-picker-border-focus);
    box-shadow: var(--date-picker-focus-shadow);
}

.themed-date-input::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: invert(13%) sepia(24%) saturate(1640%) hue-rotate(187deg) brightness(89%) contrast(94%);
    opacity: 1;
}
`;

export default GlobalStyle;